import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Client, ClientFormData } from '../../models/Client';
import { userService } from '../../services/UserService';
import { User } from '../../models/User';

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (clientId: string, clientData: Partial<ClientFormData>) => Promise<void>;
  client: Client | null;
}

export const EditClientModal: React.FC<EditClientModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  client,
}) => {
  const [formData, setFormData] = useState<ClientFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    guardianName: '',
    guardianPhone: '',
    guardianEmail: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGuardian, setHasGuardian] = useState(true);
  const [supervisors, setSupervisors] = useState<User[]>([]);

  const toIsoDate = (d?: Date | null) => (d ? new Date(d).toISOString().slice(0, 10) : '');
  const updateAuth = (key: string, val: string | number) =>
    setFormData((prev) => ({
      ...prev,
      authorizedHours: {
        weeklyHours: prev.authorizedHours?.weeklyHours ?? 0,
        payorName: prev.authorizedHours?.payorName,
        authorizationNumber: prev.authorizedHours?.authorizationNumber,
        authorizationStart: prev.authorizedHours?.authorizationStart,
        authorizationEnd: prev.authorizedHours?.authorizationEnd,
        [key]: val,
      } as ClientFormData['authorizedHours'],
    }));

  useEffect(() => {
    if (client && isOpen) {
      // Format date for display
      let dateOfBirth = '';
      if (client.dateOfBirth) {
        const date = new Date(client.dateOfBirth);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        dateOfBirth = `${month}/${day}/${year}`;
      }

      setFormData({
        firstName: client.firstName,
        lastName: client.lastName,
        dateOfBirth,
        guardianName: client.guardianName || '',
        guardianPhone: client.guardianPhone || '',
        guardianEmail: client.guardianEmail || '',
        address: client.address,
        city: client.city,
        state: client.state,
        zipCode: client.zipCode,
        notes: client.notes || '',
        assignedBcbaId: client.assignedBcbaId ?? client.primarySupervisorId ?? '',
        preferredLanguage: client.preferredLanguage || '',
        latitude: client.latitude ?? null,
        longitude: client.longitude ?? null,
        diagnosis: client.diagnosis && client.diagnosis.length ? client.diagnosis : undefined,
        authorizedHours: client.authorizedHours
          ? {
              weeklyHours: client.authorizedHours.weeklyHours ?? 0,
              payorName: client.authorizedHours.payorName ?? '',
              authorizationNumber: client.authorizedHours.authorizationNumber ?? '',
              authorizationStart: toIsoDate(client.authorizedHours.authorizationStart),
              authorizationEnd: toIsoDate(client.authorizedHours.authorizationEnd),
            }
          : undefined,
      });
      setHasGuardian(!!client.guardianName);
      userService
        .getSupervisorsByCompany(client.companyId)
        .then(setSupervisors)
        .catch(() => setSupervisors([]));
    }
  }, [client, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;

    setError(null);
    setLoading(true);

    try {
      await onSubmit(client.id, formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update client');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !client) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: 'var(--spacing-4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
            Edit Client
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-gray-400)',
              padding: 'var(--spacing-2)',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-4)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2" style={{ gap: 'var(--spacing-4)' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="firstName">
                First Name *
              </label>
              <input
                id="firstName"
                type="text"
                className="form-input"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="First name"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="lastName">
                Last Name *
              </label>
              <input
                id="lastName"
                type="text"
                className="form-input"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Last name"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="dateOfBirth">
              Date of Birth
            </label>
            <input
              id="dateOfBirth"
              type="text"
              className="form-input"
              value={formData.dateOfBirth}
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, ''); // Remove non-digits

                // Auto-format with slashes
                if (value.length >= 2) {
                  value = value.slice(0, 2) + '/' + value.slice(2);
                }
                if (value.length >= 5) {
                  value = value.slice(0, 5) + '/' + value.slice(5, 9);
                }

                setFormData({ ...formData, dateOfBirth: value });
              }}
              placeholder="MM/DD/YYYY"
              maxLength={10}
            />
          </div>

          <div className="form-group" style={{ marginTop: 'var(--spacing-4)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasGuardian}
                onChange={(e) => {
                  setHasGuardian(e.target.checked);
                  if (!e.target.checked) {
                    setFormData({
                      ...formData,
                      guardianName: '',
                      guardianPhone: '',
                      guardianEmail: '',
                    });
                  }
                }}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span className="form-label" style={{ marginBottom: 0 }}>
                Client has a parent/guardian
              </span>
            </label>
          </div>

          {hasGuardian && (
            <>
              <div
                style={{
                  marginTop: 'var(--spacing-6)',
                  marginBottom: 'var(--spacing-4)',
                  paddingTop: 'var(--spacing-4)',
                  borderTop: '1px solid var(--color-gray-200)',
                }}
              >
                <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)' }}>
                  Guardian Information
                </h3>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="guardianName">
                  Guardian Name *
                </label>
                <input
                  id="guardianName"
                  type="text"
                  className="form-input"
                  value={formData.guardianName}
                  onChange={(e) => setFormData({ ...formData, guardianName: e.target.value })}
                  placeholder="Parent or guardian name"
                  required
                />
              </div>

              <div className="grid grid-cols-2" style={{ gap: 'var(--spacing-4)' }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="guardianPhone">
                    Guardian Phone *
                  </label>
                  <input
                    id="guardianPhone"
                    type="tel"
                    className="form-input"
                    value={formData.guardianPhone}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ''); // Remove non-digits

                      // Auto-format phone number
                      if (value.length >= 3) {
                        value = '(' + value.slice(0, 3) + ') ' + value.slice(3);
                      }
                      if (value.length >= 9) {
                        value = value.slice(0, 9) + '-' + value.slice(9, 13);
                      }

                      setFormData({ ...formData, guardianPhone: value });
                    }}
                    placeholder="(555) 123-4567"
                    maxLength={14}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="guardianEmail">
                    Guardian Email
                  </label>
                  <input
                    id="guardianEmail"
                    type="email"
                    className="form-input"
                    value={formData.guardianEmail}
                    onChange={(e) => setFormData({ ...formData, guardianEmail: e.target.value })}
                    placeholder="guardian@email.com"
                  />
                </div>
              </div>
            </>
          )}

          <div
            style={{
              marginTop: 'var(--spacing-6)',
              marginBottom: 'var(--spacing-4)',
              paddingTop: 'var(--spacing-4)',
              borderTop: '1px solid var(--color-gray-200)',
            }}
          >
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)' }}>
              Service Address
            </h3>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="address">
              Street Address *
            </label>
            <input
              id="address"
              type="text"
              className="form-input"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 Main Street"
              required
            />
          </div>

          <div className="grid" style={{ gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--spacing-4)' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="city">
                City *
              </label>
              <input
                id="city"
                type="text"
                className="form-input"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="City"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="state">
                State *
              </label>
              <input
                id="state"
                type="text"
                className="form-input"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="CA"
                maxLength={2}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="zipCode">
                ZIP Code *
              </label>
              <input
                id="zipCode"
                type="text"
                className="form-input"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                placeholder="12345"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="notes">
              Notes
            </label>
            <textarea
              id="notes"
              className="form-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes about the client"
              rows={3}
            />
          </div>

          {/* Clinical & Authorization (unified schema §3) */}
          <div style={{ marginTop: 'var(--spacing-5)', paddingTop: 'var(--spacing-4)', borderTop: '1px solid var(--color-border)' }}>
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-3)' }}>
              Clinical &amp; Authorization
            </h3>

            <div className="form-group">
              <label className="form-label">Assigned BCBA</label>
              <select
                className="form-input"
                value={formData.assignedBcbaId || ''}
                onChange={(e) => setFormData({ ...formData, assignedBcbaId: e.target.value })}
              >
                <option value="">— None —</option>
                {supervisors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.credentials ? ` (${s.credentials})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--spacing-3)' }}>
              <div className="form-group">
                <label className="form-label">Diagnosis Code</label>
                <input
                  className="form-input"
                  value={formData.diagnosis?.[0]?.code || ''}
                  onChange={(e) => setFormData({ ...formData, diagnosis: [{ code: e.target.value, name: formData.diagnosis?.[0]?.name || '' }] })}
                  placeholder="F84.0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Diagnosis Name</label>
                <input
                  className="form-input"
                  value={formData.diagnosis?.[0]?.name || ''}
                  onChange={(e) => setFormData({ ...formData, diagnosis: [{ code: formData.diagnosis?.[0]?.code || '', name: e.target.value }] })}
                  placeholder="Autism Spectrum Disorder"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Preferred Language</label>
              <input
                className="form-input"
                value={formData.preferredLanguage || ''}
                onChange={(e) => setFormData({ ...formData, preferredLanguage: e.target.value })}
                placeholder="English"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)' }}>
              <div className="form-group">
                <label className="form-label">Authorized Weekly Hours</label>
                <input
                  type="number"
                  min={0}
                  className="form-input"
                  value={formData.authorizedHours?.weeklyHours ?? ''}
                  onChange={(e) => updateAuth('weeklyHours', Number(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payor</label>
                <input className="form-input" value={formData.authorizedHours?.payorName || ''} onChange={(e) => updateAuth('payorName', e.target.value)} placeholder="Medicaid" />
              </div>
              <div className="form-group">
                <label className="form-label">Authorization #</label>
                <input className="form-input" value={formData.authorizedHours?.authorizationNumber || ''} onChange={(e) => updateAuth('authorizationNumber', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Auth Start</label>
                <input type="date" className="form-input" value={formData.authorizedHours?.authorizationStart || ''} onChange={(e) => updateAuth('authorizationStart', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Auth End</label>
                <input type="date" className="form-input" value={formData.authorizedHours?.authorizationEnd || ''} onChange={(e) => updateAuth('authorizationEnd', e.target.value)} />
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              marginTop: 'var(--spacing-6)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{ flex: 1 }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={loading}
            >
              {loading ? 'Updating Client...' : 'Update Client'}
            </button>
          </div>

          <div
            style={{
              marginTop: 'var(--spacing-6)',
              padding: 'var(--spacing-4)',
              backgroundColor: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
              <strong>Privacy Note:</strong> Client information is stored securely and is only accessible to authorized staff members within your organization.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
